import { Button, Input, Select } from '@components';
import CostCenterAPICaller from '@services/api/costCenter';
import CostcenterFromToApiCaller from '@services/api/costCenterFromTo';
import { CostCenter } from 'interfaces/CostCenter';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

const { fetchCostCenterFromTo, createOrUpdate } = CostcenterFromToApiCaller;
const { listAll } = CostCenterAPICaller;

interface Props {
  closeModal: () => void;
  onSave: () => void;
  editId?: string;
  isMe?: boolean;
}

interface DefaultValue {
  id: string | null;
  from: string;
  toId: string;
  to: any;
}

const fetchDefaultValues = async ({
  editId,
}: Pick<Props, 'editId'>): Promise<DefaultValue> => {
  if (!editId) {
    return {
      id: null,
      from: '',
      toId: '',
      to: {
        value: '',
        label: '',
      },
    };
  }

  const fetchedCostCenterFromTo = await fetchCostCenterFromTo(editId);

  return {
    id: fetchedCostCenterFromTo.id || null,
    from: fetchedCostCenterFromTo.from,
    toId: fetchedCostCenterFromTo.toId,
    to: {
      value: fetchedCostCenterFromTo.to.id,
      label: fetchedCostCenterFromTo.to.name,
    },
  };
};

const CostCenterFromToFormBase = ({ editId, closeModal, onSave }: Props) => {
  const {
    register,
    setError,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isLoading, isSubmitting, defaultValues },
  } = useForm<DefaultValue>({
    defaultValues: async () => fetchDefaultValues({ editId }),
  });
  const [costCenters, setCostCenters] = useState<Array<CostCenter>>([]);

  useEffect(() => {
    loadCostCenters();
  }, []);

  const loadCostCenters = () => {
    listAll().then((response) => {
      setCostCenters(
        response.result.map((costCenter: CostCenter) => {
          return {
            label: costCenter.name,
            value: costCenter.id,
          };
        })
      );
    });
  };

  const handleOnSubmit = useCallback<SubmitHandler<DefaultValue>>(
    async (data) => {
      const { ...costCenterFromTodata } = data;

      const response = await createOrUpdate<typeof costCenterFromTodata>(
        costCenterFromTodata,
        setError
      );

      if (response.data.errors) return;

      if (!defaultValues) {
        return;
      }

      closeModal();
      onSave();
    },
    [closeModal, defaultValues, onSave, setError]
  );

  const formRef = useRef<HTMLDivElement | null>(null);

  return (
    <div ref={formRef}>
      <form autoComplete="off" onSubmit={handleSubmit(handleOnSubmit)}>
        <div className="d-flex flex-direction-column mb-s-200">
          <div className="mb-s-200">
            <Input
              disabled={isLoading}
              label="Centro de Custo de"
              error={!!errors.from}
              caption={errors.from?.message as string}
              placeholder="Digite o centro de custo de"
              form={register('from', { required: 'Obrigatório' })}
            ></Input>
          </div>

          <div className="mb-s-200">
            <Select
              label="Centro de Custo para"
              placeholder="Selecione um centro de custo"
              value={watch('to')}
              form={register('toId', { required: 'Obrigatório' })}
              disabled={isLoading}
              error={!!errors.toId}
              caption={errors.toId?.message as string}
              onSelect={(costCenter) => {
                setValue('toId', costCenter.value);
                setValue('to', costCenter);
              }}
              options={costCenters}
            ></Select>
          </div>

          <div className="d-flex justify-end">
            <Button
              disabled={isLoading}
              isLoading={isSubmitting || isLoading}
              className="pl-s-900 pr-s-900"
              type="submit"
            >
              Salvar
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

const CostCenterFromToForm = memo(CostCenterFromToFormBase);

export default CostCenterFromToForm;
